import React, { useState, useRef, useEffect } from "react";
import {
  Search,
  Bell,
  User,
  Edit,
  ChevronDown,
  Mail,
  Phone,
  Building,
  MapPin,
  Globe,
  Lock,
  Languages,
  CreditCard,
  X,
  Eye,
  EyeOff,
  Sparkles,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { SCOUT_NOTIFICATIONS_DEFAULTS } from "../../models";
import { getFormattedDate } from "../../utils/formatDate";
import { useUpdateScout } from "../../hooks/queries/useScouts";
import { useFindOrCreateAcademy } from "../../hooks/queries/useAcademies";
import { useChangePassword } from "../../hooks/queries/useAuthMutations";

const ContactData = ({
  data,
  onNotificationsClick,
  onSearchClick,
}) => {
  const [notifications, setNotifications] = useState(
    SCOUT_NOTIFICATIONS_DEFAULTS,
  );

  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [editForm, setEditForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    organization_name: "",
    address: "",
    tax_id: "",
  });

  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState(null);
  const changePassword = useChangePassword();

  const passwordChecks = [
    { label: "Al menos 8 caracteres", ok: newPassword.length >= 8 },
    { label: "Incluye letras y números", ok: /[a-zA-Z]/.test(newPassword) && /\d/.test(newPassword) },
    {
      label: "Coincide con la confirmación",
      ok: Boolean(newPassword) && newPassword === confirmPassword,
    },
    {
      label: "Distinta a la actual",
      ok: Boolean(newPassword) && newPassword !== currentPassword,
    },
  ];
  const isPasswordValid =
    Boolean(currentPassword) && passwordChecks.every((c) => c.ok);

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMessage(null);
  };

  const handleChangePassword = async () => {
    if (!isPasswordValid || changePassword.isPending) return;
    setPasswordMessage(null);
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setPasswordMessage({ kind: "success", text: "Contraseña actualizada." });
      setTimeout(closePasswordModal, 900);
    } catch (error) {
      setPasswordMessage({
        kind: "error",
        text:
          error?.status === 401
            ? "La contraseña actual no es correcta."
            : error?.message || "No se pudo cambiar la contraseña.",
      });
    }
  };

  const codeInputRefs = useRef([]);
  const updateScout = useUpdateScout();
  const findOrCreateAcademy = useFindOrCreateAcademy();

  useEffect(() => {
    if (data?.details?.notifications) {
      setNotifications(data.details.notifications);
    }
  }, [data]);

  useEffect(() => {
    const details = data?.details || {};
    const organization = details.organization_info || {};
    const preferences = details.preferences || {};

    setEditForm({
      full_name: data?.full_name || "",
      email: data?.email || "",
      phone: data?.phone || details.public_profile?.phone || "",
      country: data?.country || preferences.country || "",
      organization_name: data?.organization_name || organization.name || "",
      address: organization.address || "",
      tax_id: organization.tax_id || "",
    });
  }, [data]);

  useEffect(() => {
    if (isVerificationModalOpen) {
      codeInputRefs.current[0]?.focus();
    }
  }, [isVerificationModalOpen]);

  const notificationLabels = {
    email_alerts: "Alertas por Correo",
    push_notifications: "Notificaciones Push",
    sms_alerts: "Mensajes de Texto (SMS)",
    marketing: "Novedades y Ofertas",
    athlete_updates: "Actualizaciones de Atletas",
  };

  const toggleNotification = async (key) => {
    const previousNotifications = notifications;
    const nextNotifications = {
      ...notifications,
      [key]: !notifications[key],
    };
    // Optimista: reflejar el toggle de inmediato, luego persistir.
    setNotifications(nextNotifications);
    if (!data?.id) return;
    try {
      // details es jsonb y el PATCH reemplaza el blob completo — mergear el
      // details existente y solo sobreescribir notifications.
      await updateScout.mutateAsync({
        id: data.id,
        details: { ...(data.details || {}), notifications: nextNotifications },
      });
    } catch {
      // Revertir el optimismo si el guardado falla.
      setNotifications(previousNotifications);
    }
  };

  const handleEditChange = (field, value) => {
    setEditForm((previous) => ({ ...previous, [field]: value }));
    setEditMessage("");
  };

  const handleSaveProfile = async () => {
    if (!data?.id) {
      setEditMessage("No se encontro el perfil del scout.");
      return;
    }

    setIsSavingProfile(true);
    setEditMessage("");

    try {
      const currentDetails = data.details || {};
      const currentOrganization = currentDetails.organization_info || {};
      const currentPublicProfile = currentDetails.public_profile || {};
      const currentPreferences = currentDetails.preferences || {};
      const updatedDetails = {
        ...currentDetails,
        organization_info: {
          ...currentOrganization,
          name: editForm.organization_name,
          type: currentOrganization.type || "academy",
          address: editForm.address,
          tax_id: editForm.tax_id,
        },
        public_profile: {
          ...currentPublicProfile,
          phone: editForm.phone,
        },
        preferences: {
          ...currentPreferences,
          country: editForm.country,
        },
      };
      // Mantener scouts.academy_id sincronizado cuando cambia la organización:
      // es la fuente de verdad del gate de perfil completo, y si se queda
      // apuntando a la academia anterior el scout sigue viendo un roster que ya
      // no es el suyo. Si el nombre no cambió, conserva el id.
      let academyId = data.academy_id || null;
      const orgName = (editForm.organization_name || "").trim();
      if (orgName && orgName !== (data.organization_name || "").trim()) {
        try {
          const academy = await findOrCreateAcademy.mutateAsync({
            name: orgName,
            country: editForm.country,
          });
          academyId = academy?.id ?? academyId;
        } catch {
          // Guardar el resto del perfil vale más que abortar por la academia.
        }
      }

      // details es jsonb y el PATCH reemplaza el blob completo — ya viene
      // mergeado arriba. El email no es editable vía /scouts/:id. La mutación
      // invalida la sesión, así que el perfil nuevo llega con el refetch.
      await updateScout.mutateAsync({
        id: data.id,
        full_name: editForm.full_name,
        phone: editForm.phone,
        organization_name: editForm.organization_name,
        academy_id: academyId,
        details: updatedDetails,
      });

      setEditMessage("Informacion actualizada.");
      setTimeout(() => setIsEditModalOpen(false), 600);
    } catch (error) {
      setEditMessage(error?.message || "No se pudo guardar la informacion.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    if (value && index < 5) codeInputRefs.current[index + 1]?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  if (!data) {
    return (
      <div className="p-8 space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-64 bg-gray-200 rounded-xl"></div>
          <div className="h-64 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const {
    details: {
      organization_info = {},
      public_profile = {},
      preferences = {},
      subscription = {},
    } = {},
  } = data;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 h-full">
      <div className="p-6 md:p-8 space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="text-gray-500 text-sm font-medium">
              {getFormattedDate()}
            </p>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Bienvenido de vuelta, {data.full_name?.split(" ")[0]} 👋
            </h1>
          </div>
          <div className="hidden items-center gap-3 self-end md:self-auto lg:flex">
            <button
              type="button"
              onClick={onSearchClick}
              className="p-2 bg-white hover:bg-gray-100 rounded-lg transition-colors border border-gray-100 shadow-sm"
              aria-label="Buscar atletas"
            >
              <Search size={20} className="text-gray-600" />
            </button>
            <button
              onClick={onNotificationsClick}
              className="p-2 bg-white hover:bg-gray-100 rounded-lg transition-colors border border-gray-100 shadow-sm relative"
            >
              <Bell size={20} className="text-gray-600" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <div className="h-10 w-10 bg-blue-900 rounded-lg flex items-center justify-center text-white font-bold cursor-pointer">
              {data.full_name?.charAt(0) || <User size={20} />}
            </div>
          </div>
        </div>

        {/* Main Content - Cards in Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Mi información de contacto Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
                  <User size={20} /> Información Personal
                </h3>
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                >
                  <Edit size={18} />
                </button>
              </div>

              <div className="space-y-5">
                <div className="group">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                    Nombre Completo
                  </p>
                  <p className="text-gray-900 font-medium">{data.full_name}</p>
                </div>

                <div className="flex items-start gap-3 group">
                  <Mail size={18} className="text-blue-600 mt-1" />
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Correo electrónico
                    </p>
                    <p className="text-gray-900 font-medium break-all">
                      {data.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 group">
                  <Phone size={18} className="text-blue-600 mt-1" />
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Teléfono
                    </p>
                    <p className="text-gray-900 font-medium">
                      {data.phone || "No registrado"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <button
                  onClick={() => setIsVerificationModalOpen(true)}
                  className="w-full bg-blue-50 text-blue-700 font-semibold py-3 px-6 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle2 size={18} />
                  VERIFICAR IDENTIDAD
                </button>
              </div>
            </div>

            {/* Datos de la organización Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
                  <Building size={20} /> Organización
                </h3>
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                >
                  <Edit size={18} />
                </button>
              </div>

              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Building size={20} className="text-blue-700" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Club / Entidad
                    </p>
                    <p className="text-gray-900 font-bold">
                      {data.organization_name}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <MapPin size={12} /> Dirección
                    </p>
                    <p className="text-sm text-gray-700">
                      {organization_info.address || "Sin dirección"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <CreditCard size={12} /> Tax ID
                    </p>
                    <p className="text-sm text-gray-700">
                      {organization_info.tax_id || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Personalizar notificaciones Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-blue-900 mb-6 flex items-center gap-2">
                <Bell size={20} /> Notificaciones
              </h3>

              <div className="space-y-1 divide-y divide-gray-50">
                {Object.entries(notifications).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between py-3"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {notificationLabels[key] || key}
                    </span>
                    <button
                      onClick={() => toggleNotification(key)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                        value ? "bg-blue-900" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 shadow-sm ${
                          value ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* URL y perfil público Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
                  <Globe size={20} /> Perfil Público
                </h3>
                <div
                  className={`text-xs font-bold px-2 py-1 rounded uppercase ${public_profile.is_visible ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                >
                  {public_profile.is_visible ? "Visible" : "Oculto"}
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                <span className="text-sm text-gray-600 truncate mr-2">
                  {public_profile.url || "Generando URL..."}
                </span>
                <button className="text-blue-600 hover:text-blue-800 text-xs font-bold">
                  COPIAR
                </button>
              </div>
            </div>

            {/* Seguridad y Preferencias */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-blue-900 mb-6">
                Seguridad y Cuenta
              </h3>

              <div className="space-y-4">
                <div
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setIsPasswordModalOpen(true)}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-700">
                      <Lock size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        Contraseña
                      </p>
                      <p className="text-xs text-gray-500">
                        Último cambio hace 3 meses
                      </p>
                    </div>
                  </div>
                  <Edit size={16} className="text-gray-400" />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg text-purple-700">
                      <Languages size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">Idioma</p>
                      <p className="text-xs text-gray-500">
                        {preferences.language || "Español"}
                      </p>
                    </div>
                  </div>
                  <ChevronDown size={16} className="text-gray-400" />
                </div>
              </div>
            </div>

            {/* Plan de Suscripción Actual Card */}
            <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-6 shadow-lg text-white">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-bold text-white/90">Tu Plan</h3>
                  <p className="text-2xl font-bold mt-1 text-white">
                    {subscription.plan_name || "Free Tier"}
                  </p>
                </div>
                <div className="bg-white/10 px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm">
                  {subscription.status === "active" ? "ACTIVO" : "INACTIVO"}
                </div>
              </div>

              <div className="space-y-4 bg-white/5 p-4 rounded-lg mb-6">
                <div>
                  <p className="text-xs text-blue-200 mb-1">
                    Próxima facturación
                  </p>
                  <p className="font-semibold">
                    {subscription.next_payment || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-200 mb-2">
                    Beneficios incluidos:
                  </p>
                  <ul className="space-y-2">
                    {subscription.benefits?.map((benefit, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle2
                          size={16}
                          className="text-green-400 mt-0.5 flex-shrink-0"
                        />
                        <span className="text-blue-50">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <button className="w-full bg-white text-blue-900 font-bold py-3 px-6 rounded-lg hover:bg-blue-50 transition-colors shadow-sm">
                GESTIONAR PAGOS (PSE)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Los modales se mantienen igual, solo asegúrate de que están renderizados aquí abajo */}
      {/* Verification Modal ... */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsEditModalOpen(false)}
          ></div>
          <div className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:max-w-2xl sm:rounded-2xl sm:p-8 sm:max-h-[90vh]">
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
            >
              <X size={20} className="text-gray-500" />
            </button>
            <h3 className="text-xl font-bold text-blue-900 mb-1">
              Editar informacion
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Actualiza tus datos de contacto, residencia y organizacion.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["full_name", "Nombre completo", "text"],
                ["email", "Correo electronico", "email", true],
                ["phone", "Telefono", "tel"],
                ["country", "Pais de residencia", "text"],
                ["organization_name", "Academia / club afiliado", "text"],
                ["address", "Direccion", "text"],
                ["tax_id", "Tax ID", "text"],
              ].map(([field, label, type, readOnly]) => (
                <label key={field} className="space-y-2">
                  <span className="text-sm font-semibold text-blue-900">
                    {label}
                  </span>
                  <input
                    type={type}
                    value={editForm[field]}
                    onChange={readOnly ? undefined : (event) =>
                      handleEditChange(field, event.target.value)
                    }
                    readOnly={!!readOnly}
                    className={`w-full rounded-lg border px-3 py-2 ${
                      readOnly
                        ? "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-600"
                    }`}
                  />
                </label>
              ))}
            </div>

            {editMessage && (
              <p className="mt-5 text-center text-sm font-medium text-gray-700">
                {editMessage}
              </p>
            )}

            <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-5">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="rounded-lg bg-gray-100 px-5 py-2 font-semibold text-gray-700 transition-colors hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={isSavingProfile}
                className="flex items-center gap-2 rounded-lg bg-blue-900 px-6 py-2 font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSavingProfile ? (
                  <>
                    Guardando
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </>
                ) : (
                  "Guardar cambios"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isVerificationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsVerificationModalOpen(false)}
          ></div>
          <div className="relative bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setIsVerificationModalOpen(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
            >
              <X size={20} className="text-gray-500" />
            </button>
            <h3 className="text-xl font-bold text-center mb-2">
              Verificación de Seguridad
            </h3>
            <p className="text-center text-gray-500 text-sm mb-6">
              Ingresa el código de 6 dígitos enviado a <br />
              <span className="font-semibold text-gray-900">{data.email}</span>
            </p>

            <div className="flex gap-2 justify-center mb-6">
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (codeInputRefs.current[index] = el)}
                  type="text"
                  maxLength={1}
                  className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                />
              ))}
            </div>

            <button className="w-full bg-blue-900 text-white font-bold py-3 rounded-lg hover:bg-blue-800 transition-colors flex items-center justify-center gap-2">
              VERIFICAR AHORA <Sparkles size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Password Modal - (Mismo estilo que el de arriba) */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closePasswordModal}
          ></div>
          <div className="relative bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <button
              onClick={closePasswordModal}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
            >
              <X size={20} className="text-gray-500" />
            </button>
            <h3 className="text-xl font-bold mb-6">Cambiar contraseña</h3>
            <div className="space-y-4">
              {/* La actual es obligatoria: el backend re-verifica antes de
                  cambiar, así que una sesión prestada no basta. */}
              <input
                type="password"
                placeholder="Contraseña actual"
                autoComplete="current-password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-600"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Nueva contraseña"
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-600"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-3 text-gray-400"
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <input
                type="password"
                placeholder="Confirmar nueva contraseña"
                autoComplete="new-password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-600"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              {/* Checklist en vivo: decir los requisitos al fallar obliga a
                  adivinar; mostrarlos mientras se escribe, no. */}
              <ul className="space-y-1 text-xs">
                {passwordChecks.map((check) => (
                  <li
                    key={check.label}
                    className={check.ok ? "text-green-700" : "text-gray-500"}
                  >
                    {check.ok ? "✓" : "○"} {check.label}
                  </li>
                ))}
              </ul>

              {passwordMessage && (
                <p
                  className={`text-sm ${
                    passwordMessage.kind === "error"
                      ? "text-red-600"
                      : "text-green-700"
                  }`}
                >
                  {passwordMessage.text}
                </p>
              )}

              <button
                type="button"
                onClick={handleChangePassword}
                disabled={!isPasswordValid || changePassword.isPending}
                className="w-full bg-blue-900 text-white font-bold py-3 rounded-lg hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {changePassword.isPending ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactData;
